import type { Components, Theme } from '@mui/material';
import { chipBorderd, chipLg, chipMd, chipSm } from 'components/chip';
import {
  primary,
  secondary,
  tertiary,
  tertiaryOutline,
  quaternary,
  linkButton,
  lgButton,
  mdButton,
  smButton,
  linkBlack,
  linkWhite,
  smLinkButton,
  mdLinkButton,
  xlLinkButton,
  lgLinkButton,
  lgIconButton,
  mdIconButton,
  smIconButton,
} from 'components/button';

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    large: false;
    medium: false;
    small: false;
    lg: true;
    md: true;
    sm: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    tertiary: true;
    quaternary: true;
    link: true;
    linkBlack: true;
    linkWhite: true;
    tertiaryOutline: true;
  }
  interface ButtonPropsSizeOverrides {
    large: false;
    medium: false;
    small: false;
    lg: true;
    md: true;
    sm: true;
    xl: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipOwnProps {
    isBordered?: boolean;
    isBackgroundFilled?: boolean;
  }

  interface ChipPropsColorOverrides {
    default: false;
    primary: false;
    secondary: false;
    error: false;
    info: false;
    success: false;
    warning: false;

    white: true;
    blue: true;
    coral: true;
    grey: true;
    lavender: true;
    red: true;
    yellow: true;
    brand: true;
  }
  interface ChipPropsSizeOverrides {
    medium: false;
    small: false;
    lg: true;
    md: true;
    sm: true;
  }
}

export const components: Components<Omit<Theme, 'components'>> | undefined = {
  MuiChip: {
    defaultProps: { size: 'md', isBordered: false },
    variants: [
      { props: { size: 'sm' }, style: chipSm },
      { props: { size: 'md' }, style: chipMd },
      { props: { size: 'lg' }, style: chipLg },
      { props: { isBordered: true }, style: chipBorderd },
      { props: { isBordered: false }, style: { border: 'none' } },
    ],
  },

  MuiTypography: {
    defaultProps: { textVariant: 'regular' },
    variants: [
      {
        props: { textVariant: 'regular' },
        style: {
          fontWeight: 400,
        },
      },
      {
        props: { textVariant: 'medium' },
        style: {
          fontWeight: 500,
        },
      },
      {
        props: { textVariant: 'semiBold' },
        style: {
          fontWeight: 600,
        },
      },
      {
        props: { textVariant: 'bold' },
        style: {
          fontWeight: 700,
        },
      },
    ],
  },
  MuiIconButton: {
    defaultProps: { size: 'sm', disableRipple: true },
    variants: [
      {
        props: { size: 'sm' },
        style: smIconButton,
      },
      {
        props: { size: 'md' },
        style: mdIconButton,
      },
      {
        props: { size: 'lg' },
        style: lgIconButton,
      },
    ],
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      size: 'sm',
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontSize: '0.875rem',
        lineHeight: '20px',
        minWidth: '100px',
        gap: '8px',
        fontWeight: 400,
      },
    },
    variants: [
      {
        props: { size: 'sm' },
        style: smButton,
      },
      {
        props: { size: 'md' },
        style: mdButton,
      },
      {
        props: { size: 'lg' },
        style: lgButton,
      },
      {
        props: { variant: 'primary' },
        style: primary,
      },
      {
        props: { variant: 'secondary' },
        style: secondary,
      },
      {
        props: { variant: 'tertiary' },
        style: tertiary,
      },
      {
        props: { variant: 'tertiaryOutline' },
        style: tertiaryOutline,
      },
      {
        props: { variant: 'quaternary' },
        style: quaternary,
      },
      {
        props: { variant: 'link' },
        style: linkButton,
      },
      {
        props: { variant: 'linkBlack' },
        style: linkBlack,
      },
      {
        props: { variant: 'linkWhite' },
        style: linkWhite,
      },
      {
        props: { variant: 'link', size: 'sm' },
        style: smLinkButton,
      },
      {
        props: { variant: 'linkBlack', size: 'sm' },
        style: smLinkButton,
      },
      {
        props: { variant: 'linkWhite', size: 'sm' },
        style: smLinkButton,
      },
      {
        props: { variant: 'link', size: 'md' },
        style: mdLinkButton,
      },
      {
        props: { variant: 'linkBlack', size: 'md' },
        style: mdLinkButton,
      },
      {
        props: { variant: 'linkWhite', size: 'md' },
        style: mdLinkButton,
      },
      {
        props: { variant: 'link', size: 'lg' },
        style: lgLinkButton,
      },
      {
        props: { variant: 'linkBlack', size: 'lg' },
        style: lgLinkButton,
      },
      {
        props: { variant: 'linkWhite', size: 'lg' },
        style: lgLinkButton,
      },
      {
        props: { variant: 'link', size: 'xl' },
        style: xlLinkButton,
      },
      {
        props: { variant: 'linkBlack', size: 'xl' },
        style: xlLinkButton,
      },
      {
        props: { variant: 'linkWhite', size: 'xl' },
        style: xlLinkButton,
      },
    ],
  },
};
